import { useEffect, useMemo, useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Bell, CheckCheck, ExternalLink, Loader2, MessageCircleMore, RefreshCw, Trash2, X } from 'lucide-react'
import { formatDistanceToNow } from 'date-fns'
import { fr } from 'date-fns/locale'
import { useNavigate } from 'react-router-dom'
import toast from 'react-hot-toast'
import { deleteNotification, getNotifications, markNotificationAsRead } from '../../services/api.js'

function NotificationEntry({ notification, onOpen, onDelete, onToggleRead }) {
  const formattedDate = useMemo(() => {
    if (!notification.createdAt) return 'à l’instant'

    try {
      return formatDistanceToNow(new Date(notification.createdAt), {
        addSuffix: true,
        locale: fr,
      })
    } catch {
      return 'à l’instant'
    }
  }, [notification.createdAt])

  const externalLink = notification.externalLink || ''
  const linkLabel = externalLink.startsWith('/dashboard/projects-auto')
    ? 'Ouvrir le projet auto'
    : externalLink.startsWith('http')
      ? 'Ouvrir le lien'
      : 'Voir le détail'

  return (
    <div
      className={`group flex cursor-pointer gap-3 border-b border-ink/5 px-4 py-3 transition hover:bg-ink/[0.03] ${notification.notifRead ? 'bg-transparent' : 'bg-brand/[0.06]'}`}
      onClick={() => onOpen(notification)}
      role="button"
      tabIndex={0}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault()
          onOpen(notification)
        }
      }}
    >
      <div className={`mt-1 flex h-9 w-9 shrink-0 items-center justify-center rounded-2xl border ${notification.notifRead ? 'border-ink/10 bg-ink/[0.03] text-ink/55' : 'border-brand/25 bg-brand/10 text-brand-text'}`}>
        <MessageCircleMore className="h-4 w-4" />
      </div>

      <div className="min-w-0 flex-1">
        <div className="flex items-start justify-between gap-3">
          <div>
            <p className={`text-sm font-semibold ${notification.notifRead ? 'text-ink/80' : 'text-ink'}`}>{notification.title}</p>
            <p className="mt-1 text-sm leading-6 text-ink/55">{notification.message}</p>
          </div>
          <span className="shrink-0 text-[11px] text-ink/30">{formattedDate}</span>
        </div>

        {externalLink && (
          <div className="mt-2 flex flex-wrap items-center gap-2 text-[11px] text-brand-text">
            <ExternalLink className="h-3.5 w-3.5" />
            <span>{linkLabel}</span>
          </div>
        )}
      </div>

      <div className="flex shrink-0 flex-col gap-2 opacity-0 transition group-hover:opacity-100">
        {!notification.notifRead && (
          <button
            type="button"
            onClick={(event) => {
              event.stopPropagation()
              onToggleRead(notification)
            }}
            className="flex h-8 w-8 items-center justify-center rounded-xl border border-ink/10 bg-ink/[0.03] text-ink/60 transition hover:border-brand/30 hover:text-brand-text"
            title="Marquer comme lue"
          >
            <CheckCheck className="h-4 w-4" />
          </button>
        )}
        <button
          type="button"
          onClick={(event) => {
            event.stopPropagation()
            onDelete(notification)
          }}
          className="flex h-8 w-8 items-center justify-center rounded-xl border border-ink/10 bg-ink/[0.03] text-ink/60 transition hover:border-red-500/30 hover:text-red-400"
          title="Supprimer"
        >
          <Trash2 className="h-4 w-4" />
        </button>
      </div>
    </div>
  )
}

export default function NotificationsMenu() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const menuRef = useRef(null)
  const [isOpen, setIsOpen] = useState(false)

  const notificationsQuery = useQuery({
    queryKey: ['notifications', { page: 1, itemsPerPage: 12 }],
    queryFn: () => getNotifications({ page: 1, itemsPerPage: 12 }),
    refetchInterval: 60000,
    staleTime: 30000,
  })

  const unreadQuery = useQuery({
    queryKey: ['notifications', { unread: true }],
    queryFn: () => getNotifications({ page: 1, itemsPerPage: 1, notifRead: false }),
    refetchInterval: 60000,
    staleTime: 30000,
  })

  const notifications = useMemo(() => {
    const items = notificationsQuery.data?.notifications ?? []
    return [...items].sort((a, b) => {
      const dateA = a.createdAt ? new Date(a.createdAt).getTime() : 0
      const dateB = b.createdAt ? new Date(b.createdAt).getTime() : 0
      return dateB - dateA
    })
  }, [notificationsQuery.data])

  const unreadCount = unreadQuery.data?.totalItems ?? 0

  const markReadMutation = useMutation({
    mutationFn: async (notification) => {
      if (notification.notifRead) return notification
      return markNotificationAsRead(notification.id)
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['notifications'] })
    },
    onError: () => {
      toast.error('Impossible de marquer la notification comme lue')
    },
  })

  const deleteMutation = useMutation({
    mutationFn: async (notification) => deleteNotification(notification.id),
    onSuccess: async () => {
      toast.success('Notification supprimée')
      await queryClient.invalidateQueries({ queryKey: ['notifications'] })
    },
    onError: () => {
      toast.error('Impossible de supprimer la notification')
    },
  })

  const closeMenu = () => setIsOpen(false)

  const handleOpen = async (notification) => {
    await markReadMutation.mutateAsync(notification).catch(() => {})

    const link = notification.externalLink
    closeMenu()

    if (!link) return

    if (link.startsWith('http')) {
      window.open(link, '_blank', 'noopener,noreferrer')
      return
    }

    navigate(link)
  }

  const handleMarkAllAsRead = async () => {
    const unreadNotifications = notifications.filter((notification) => !notification.notifRead)
    if (unreadNotifications.length === 0) return

    try {
      await Promise.all(unreadNotifications.map((notification) => markNotificationAsRead(notification.id)))
      await queryClient.invalidateQueries({ queryKey: ['notifications'] })
      toast.success('Notifications marquées comme lues')
    } catch {
      toast.error('Impossible de tout marquer comme lu')
    }
  }

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setIsOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  return (
    <div className="relative" ref={menuRef}>
      <button
        type="button"
        onClick={() => setIsOpen((value) => !value)}
        className="relative flex h-11 w-11 items-center justify-center rounded-2xl border border-ink/10 bg-ink/[0.03] text-ink/70 transition hover:border-brand/25 hover:text-brand-text"
        aria-label="Notifications"
      >
        <Bell className="h-5 w-5" />
        {unreadCount > 0 && (
          <span className="absolute -right-1 -top-1 flex min-w-5 items-center justify-center rounded-full bg-brand px-1.5 py-0.5 text-[10px] font-bold text-on-brand">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {isOpen && (
        <div className="absolute right-0 z-[70] mt-3 w-[22rem] overflow-hidden rounded-[14px] border border-ink/10 bg-panel shadow-[0_24px_60px_rgb(var(--rgb-shadow)/calc(0.55*var(--shadow-k)))]">
          <div className="flex items-start justify-between gap-4 border-b border-ink/10 px-4 py-4">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.22em] text-brand-text">Notifications</p>
              <h3 className="mt-1 text-base font-semibold text-ink">JLP</h3>
              <p className="mt-1 text-sm text-ink/45">Rappels et suivi des projets</p>
            </div>
            <button
              type="button"
              onClick={closeMenu}
              className="flex h-9 w-9 items-center justify-center rounded-xl border border-ink/10 bg-ink/[0.03] text-ink/60 transition hover:text-ink"
              aria-label="Fermer"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          <div className="flex items-center justify-between border-b border-ink/5 px-4 py-3 text-xs text-ink/45">
            <span>{unreadCount} non lue{unreadCount > 1 ? 's' : ''}</span>
            <button
              type="button"
              onClick={handleMarkAllAsRead}
              className="inline-flex items-center gap-1.5 text-brand-text transition hover:brightness-110"
            >
              <CheckCheck className="h-3.5 w-3.5" />
              Tout lire
            </button>
          </div>

          <div className="max-h-[28rem] overflow-y-auto">
            {notificationsQuery.isLoading && (
              <div className="flex items-center justify-center px-4 py-8 text-ink/50">
                <Loader2 className="h-5 w-5 animate-spin" />
              </div>
            )}

            {notificationsQuery.isError && (
              <div className="px-4 py-8 text-center text-sm text-red-400">
                Impossible de charger les notifications.
              </div>
            )}

            {!notificationsQuery.isLoading && !notificationsQuery.isError && notifications.length === 0 && (
              <div className="px-4 py-10 text-center text-sm text-ink/40">
                Aucune notification pour le moment.
              </div>
            )}

            {notifications.map((notification) => (
              <NotificationEntry
                key={notification.id}
                notification={notification}
                onOpen={handleOpen}
                onDelete={(item) => deleteMutation.mutate(item)}
                onToggleRead={(item) => markReadMutation.mutate(item)}
              />
            ))}
          </div>

          <div className="flex items-center justify-between border-t border-ink/10 px-4 py-3 text-xs text-ink/45">
            <span>Actualisation automatique toutes les minutes</span>
            <button
              type="button"
              onClick={() => {
                queryClient.invalidateQueries({ queryKey: ['notifications'] })
                toast.success('Notifications rafraîchies')
              }}
              className="inline-flex items-center gap-1.5 text-ink/65 transition hover:text-ink"
            >
              <RefreshCw className="h-3.5 w-3.5" />
              Rafraîchir
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
